<script>
import axios from '@/utils/axios';
import { useLeadMetaStore } from '@/stores/useLeadMetaStore';

export default {
  inject: ['setLoading', 'auth'],
  data() {
    return {
      leadMeta: useLeadMetaStore(),
      loading: true,
      tableColumns: [
        { key: 'branch', label: 'Branch' },
        { key: 'late_to_halfday', label: 'Late → Half-day' },
        { key: 'halfday_to_absent', label: 'Half-day → Absent' },
        { key: 'effective', label: 'Effective Period' },
        { key: 'is_active', label: 'Active' },
        { key: 'created_at', label: 'Created' },
        { key: 'actions', label: 'Actions', width: '80px' },
      ],
      policies: [],
      branchMap: {},
      search: '',
      pagination: {
        current_page: 1,
        per_page: 50,
        last_page: 1,
        total: 0,
        links: [],
      },
    };
  },
  mounted() {
    this.loadBranches();
    this.fetchPolicies();
  },
  methods: {
    async loadBranches() {
      try {
        // sirf branches chahiye; agar store me nahi hain to sirf branches fetch karo
        if (!this.leadMeta.branches || !this.leadMeta.branches.length) {
          await this.leadMeta.refreshBranches();
        }
        this.branchMap = {};
        (this.leadMeta.branches || []).forEach((b) => {
          this.branchMap[b.id] = b.name || b.branch_name || b.title || `Branch #${b.id}`;
        });
      } catch (error) {
        console.log(error.message);
      }
    },

    async fetchPolicies(page = 1) {
      this.loading = true;
      try {
        const response = await axios.get('/hrms/attendance-policies', {
          params: {
            page,
            search: this.search || undefined,
          },
        });
        this.policies = response.data.data.data;
        this.pagination = {
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          last_page: response.data.data.last_page,
          total: response.data.data.total,
          links: response.data.data.links,
        };
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    onSearch() {
      this.fetchPolicies(1);
    },

    branchName(branch_id) {
      if (!branch_id) return '-';
      return this.branchMap[branch_id] || '-';
    },

    async toggleActive(policy) {
      this.setLoading(true);
      try {
        const res = await axios.patch(`/hrms/attendance-policies/${policy.id}`);
        this.$swalToast(res.data.message);
        this.fetchPolicies(this.pagination.current_page);
      } catch (error) {
        this.$swalToast(error.message, 'error');
        // revert UI toggle on failure
        policy.is_active = !policy.is_active;
      } finally {
        this.setLoading(false);
      }
    },

    openAdd() {
      this.$refs.policyModal.add();
      this.$openModal('hrm_attendance_policy_modal');
    },

    openEdit(policy) {
      this.$refs.policyModal.edit(policy);
      this.$openModal('hrm_attendance_policy_modal');
    },

    confirmDelete(policy) {
      this.$refs.policyModal.setDelete(policy);
      this.$openModal('hrm_attendance_policy_delete_modal');
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Header -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <div>
          <h4 class="page-title mb-0">Attendance Policies</h4>
        </div>
        <div class="d-flex align-items-center flex-wrap gap-2">
          <a href="javascript:void(0);" class="btn btn-primary d-flex align-items-center" @click="openAdd">
            <i class="ti ti-circle-plus me-2"></i>Add Policy
          </a>
        </div>
      </div>

      <!-- Main Card -->
      <div class="card border-0 shadow-sm mb-0" style="border-radius:14px;">

        <!-- Card Header -->
        <div class="card-header bg-white border-bottom d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4">
          <h5 class="mb-0 fw-bold" style="font-size:14px; color:#0f172a;">
            Attendance Policy List
            <span style="font-size:11px; color:#94a3b8; font-weight:500; margin-left:6px;">({{ pagination.total }})</span>
          </h5>

          <!-- Search -->
          <div class="d-flex align-items-center gap-2">
            <div class="l-search-wrap">
              <input type="text" class="l-search-input" v-model="search" placeholder="Search policies..." @keyup.enter="onSearch" />
              <span style="padding:0 10px; color:#94a3b8;"><i class="ti ti-search"></i></span>
            </div>
          </div>
        </div>

        <!-- Card Body — Table -->
        <div class="card-body px-3 pt-2 pb-3" style="background:#f1f5f9;">

          <app-table
            :columns="tableColumns"
            :rows="policies"
            :pagination="pagination"
            :loading="loading"
            empty-text="No attendance policies found."
            empty-icon="ti ti-clock-check"
            @page-change="fetchPolicies"
          >
            <!-- Branch -->
            <template #branch="{ row }">
              <span style="font-size:12.5px; font-weight:600; color:#1e293b;">{{ branchName(row.branch_id) }}</span>
            </template>

            <!-- Late to half-day -->
            <template #late_to_halfday="{ row }">
              <span class="ap-count">{{ row.late_to_halfday_count }}</span>
              <span style="font-size:11px; color:#94a3b8;"> late(s)</span>
            </template>

            <!-- Half-day to absent -->
            <template #halfday_to_absent="{ row }">
              <span class="ap-count">{{ row.halfday_to_absent_count }}</span>
              <span style="font-size:11px; color:#94a3b8;"> half-day(s)</span>
            </template>

            <!-- Effective period -->
            <template #effective="{ row }">
              <span style="font-size:11.5px; color:#475569;">
                {{ $formatDate(row.effective_from) }}
                <template v-if="row.effective_to"> — {{ $formatDate(row.effective_to) }}</template>
                <template v-else> <span style="color:#16a34a; font-weight:600;">(ongoing)</span></template>
              </span>
            </template>

            <!-- Active toggle -->
            <template #is_active="{ row }">
              <div class="form-check form-switch mb-0">
                <input
                  class="form-check-input"
                  type="checkbox"
                  role="switch"
                  style="cursor:pointer;"
                  :checked="!!row.is_active"
                  @change="toggleActive(row)"
                />
              </div>
            </template>

            <!-- Created -->
            <template #created_at="{ row }">
              <span style="font-size:11.5px; color:#94a3b8;">{{ $formatDate(row.created_at) }}</span>
            </template>

            <!-- Actions -->
            <template #actions="{ row }">
              <div class="dropdown">
                <button class="btn btn-icon btn-sm rounded-circle" type="button" data-bs-toggle="dropdown">
                  <i class="ti ti-dots-vertical"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="openEdit(row)">
                      <i class="ti ti-edit me-1"></i>Edit
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item rounded-1 text-danger" href="javascript:void(0);" @click="confirmDelete(row)">
                      <i class="ti ti-trash me-1"></i>Delete
                    </a>
                  </li>
                </ul>
              </div>
            </template>
          </app-table>

        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <hrm-attendance-policy-modal ref="policyModal" @updated="fetchPolicies" />
</template>

<style scoped>
.l-search-wrap {
  display: flex;
  align-items: center;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.l-search-wrap:focus-within {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, .1);
}

.l-search-input {
  border: none;
  outline: none;
  height: 34px;
  padding: 0 10px;
  font-size: 12.5px;
  color: #1e293b;
  width: 200px;
  background: transparent;
}

.ap-count {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}
</style>